import { generatePath } from "react-router-dom";
import { apiRoutes } from "../apiRoutes";
import axios from "../appAxios";

export const getAdminDashboard = async () => {
  const res = await axios.get(apiRoutes.ADMIN_DASHBOARD);
  return res.data;
};

export const getAdminAnalyticsSummary = async () => {
  const res = await axios.get(apiRoutes.ADMIN_ANALYTICS_SUMMARY);
  return res.data;
};

export const getAdminDisputes = async ({
  page = 1,
  limit = 20,
  sort = "newest",
  resolved = false,
} = {}) => {
  const res = await axios.get(apiRoutes.ADMIN_DISPUTES, {
    params: { page, limit, sort, resolved },
  });
  return res.data;
};

export const getAdminDisputeDetail = async (orderId) => {
  const res = await axios.get(
    generatePath(apiRoutes.ADMIN_DISPUTE_DETAIL, { orderId }),
  );
  return res.data;
};

export const resolveDispute = async (
  orderId,
  { resolution, adminNotes, resolutionMessage },
) => {
  const res = await axios.post(
    generatePath(apiRoutes.ADMIN_RESOLVE_DISPUTE, { orderId }),
    { resolution, adminNotes, resolutionMessage },
  );
  return res.data;
};

export const getAdminReports = async ({
  page = 1,
  limit = 20,
  status,
  entityType,
} = {}) => {
  const res = await axios.get(apiRoutes.ADMIN_REPORTS, {
    params: { page, limit, status, entityType },
  });
  return res.data;
};

export const resolveReport = async (reportId, { status, adminNotes }) => {
  const res = await axios.post(
    generatePath(apiRoutes.ADMIN_RESOLVE_REPORT, { reportId }),
    { status, adminNotes },
  );
  return res.data;
};

export const getAdminUsers = async ({
  page = 1,
  limit = 20,
  accountStatus,
  search,
} = {}) => {
  const res = await axios.get(apiRoutes.ADMIN_USERS, {
    params: { page, limit, accountStatus, search },
  });
  return res.data;
};

export const getAdminUserDetail = async (userId) => {
  const res = await axios.get(
    generatePath(apiRoutes.ADMIN_USER_DETAIL, { userId }),
  );
  return res.data;
};

export const updateUserStatus = async (userId, { status, reason }) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_USER_STATUS, { userId }),
    { status, reason },
  );
  return res.data;
};

export const updateUserRole = async (userId, { isAdmin }) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_USER_ROLE, { userId }),
    { isAdmin },
  );
  return res.data;
};

export const getAdminShops = async ({
  page = 1,
  limit = 20,
  status,
  search,
} = {}) => {
  const res = await axios.get(apiRoutes.ADMIN_SHOPS, {
    params: { page, limit, status, search },
  });
  return res.data;
};

export const getAdminShopDetail = async (shopId) => {
  const res = await axios.get(
    generatePath(apiRoutes.ADMIN_SHOP_DETAIL, { shopId }),
  );
  return res.data;
};

export const getAdminOrders = async ({
  page = 1,
  limit = 20,
  status,
  search,
  sort = "newest",
  negotiation,
} = {}) => {
  const res = await axios.get(apiRoutes.ADMIN_ORDERS, {
    params: { page, limit, status, search, sort, negotiation },
  });
  return res.data;
};

export const getAdminOrderDetail = async (orderId) => {
  const res = await axios.get(
    generatePath(apiRoutes.ADMIN_ORDER_DETAIL, { orderId }),
  );
  return res.data;
};

export const getAdminProducts = async ({
  page = 1,
  limit = 20,
  status,
  category,
  search,
  sort = "newest",
} = {}) => {
  const res = await axios.get(apiRoutes.ADMIN_PRODUCTS, {
    params: { page, limit, status, category, search, sort },
  });
  return res.data;
};

export const getAdminProductDetail = async (productId) => {
  const res = await axios.get(
    generatePath(apiRoutes.ADMIN_PRODUCT_DETAIL, { productId }),
  );
  return res.data;
};

export const updateShopStatus = async (shopId, { status, reason }) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_SHOP_STATUS, { shopId }),
    { status, reason },
  );
  return res.data;
};

export const updateProductStatus = async (productId, { status }) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_PRODUCT_STATUS, { productId }),
    { status },
  );
  return res.data;
};

export const resetUserChangeLimit = async (userId, { field }) => {
  const res = await axios.post(
    generatePath(apiRoutes.ADMIN_USER_RESET_LIMIT, { userId }),
    { field },
  );
  return res.data;
};

export const clearDeletionCooldown = async (userId) => {
  const res = await axios.post(
    generatePath(apiRoutes.ADMIN_USER_CLEAR_COOLDOWN, { userId }),
  );
  return res.data;
};

export const resetShopChangeLimit = async (shopId, { field }) => {
  const res = await axios.post(
    generatePath(apiRoutes.ADMIN_SHOP_RESET_LIMIT, { shopId }),
    { field },
  );
  return res.data;
};

export const resetProductChangeLimit = async (productId, { field }) => {
  const res = await axios.post(
    generatePath(apiRoutes.ADMIN_PRODUCT_RESET_LIMIT, { productId }),
    { field },
  );
  return res.data;
};

export const getAdminVerifications = async ({
  page = 1,
  limit = 20,
  status = "PENDING",
} = {}) => {
  const res = await axios.get(apiRoutes.ADMIN_VERIFICATIONS, {
    params: { page, limit, status },
  });
  return res.data;
};

export const getAdminVerificationDetail = async (userId) => {
  const res = await axios.get(
    generatePath(apiRoutes.ADMIN_VERIFICATION_DETAIL, { userId }),
  );
  return res.data;
};

export const reviewVerification = async (
  userId,
  { status, rejectionReason },
) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_VERIFICATION_REVIEW, { userId }),
    { status, rejectionReason },
  );
  return res.data;
};

export const flagUserForVerification = async (userId, { required }) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_USER_FLAG_VERIFICATION, { userId }),
    { required },
  );
  return res.data;
};

export const flagShopForVerification = async (shopId, { required }) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_SHOP_FLAG_VERIFICATION, { shopId }),
    { required },
  );
  return res.data;
};

export const setUserRecommendationVisibility = async (userId, { hidden }) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_USER_RECOMMENDATIONS, { userId }),
    { hidden },
  );
  return res.data;
};

export const setShopRecommendationVisibility = async (shopId, { hidden }) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_SHOP_RECOMMENDATIONS, { shopId }),
    { hidden },
  );
  return res.data;
};

// Revoking a shop's hidden prices also archives its live hidden price products.
export const setShopOffersMode = async (shopId, { revoked }) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_SHOP_OFFERS_MODE, { shopId }),
    { revoked },
  );
  return res.data;
};

export const setProductRecommendationVisibility = async (
  productId,
  { hidden },
) => {
  const res = await axios.patch(
    generatePath(apiRoutes.ADMIN_PRODUCT_RECOMMENDATIONS, { productId }),
    { hidden },
  );
  return res.data;
};

export const getAdminFeedback = async ({ page = 1, limit = 20, type } = {}) => {
  const res = await axios.get(apiRoutes.ADMIN_FEEDBACK, {
    params: { page, limit, type },
  });
  return res.data;
};
